// src/components/EntityNavigation.js
// Unified Navigation & Filter Component - entity-level navigation with integrated header
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
    RiSearchLine, 
    RiArrowDownSLine, 
    RiBriefcaseLine, 
    RiUserLine, 
    RiBuildingLine,
    RiHome4Line,
    RiFlagLine,
    RiErrorWarningLine,
    RiCheckboxCircleLine,
    RiArrowLeftLine,
    RiCheckLine,
    RiLogoutBoxRLine,
    RiArrowRightSLine,
    RiMessage3Line,
    RiFilterLine
} from 'react-icons/ri';
import { useEntityNavigation } from '../hooks/useEntityNavigation';
import { ROUTES } from '../constants/routes';
import { formatEmailDisplayName, getInitialsFromEmail } from '../utils';
import apollo from '../styles/apolloTheme';

// Entity icon component
const EntityIcon = ({ type, className = "w-4 h-4" }) => {
    switch (type) {
        case 'client':
            return <RiBriefcaseLine className={className} />;
        case 'department':
            return <RiBuildingLine className={className} />;
        case 'staff':
            return <RiUserLine className={className} />;
        default:
            return <RiBriefcaseLine className={className} />;
    }
};

// Navigation tabs for entity sub-views
const NAV_TABS = {
    client: [
        { id: 'overview', label: 'Overview', icon: RiHome4Line },
        { id: 'goals', label: 'Goals', icon: RiFlagLine },
        { id: 'issues', label: 'Issues', icon: RiErrorWarningLine },
        { id: 'action-items', label: 'Actions', icon: RiCheckboxCircleLine },
        { id: 'sentiment', label: 'Comms', icon: RiMessage3Line },
    ],
    department: [
        { id: 'overview', label: 'Overview', icon: RiHome4Line },
        { id: 'goals', label: 'Goals', icon: RiFlagLine },
        { id: 'issues', label: 'Issues', icon: RiErrorWarningLine },
        { id: 'action-items', label: 'Actions', icon: RiCheckboxCircleLine },
        { id: 'sentiment', label: 'Comms', icon: RiMessage3Line },
    ],
    staff: [
        { id: 'overview', label: 'Overview', icon: RiHome4Line },
    ],
};

// User Menu component
const UserMenu = ({ user, signOut }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const getUserDisplayName = () => formatEmailDisplayName(user?.email, 'User');

    const getUserInitials = () => getInitialsFromEmail(user?.email, 'U');

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-2 px-2 py-1.5 rounded-lg transition-all hover:bg-gray-50"
                data-testid="user-menu-button"
            >
                <div 
                    className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0"
                    style={{ 
                        backgroundColor: apollo.color.primary,
                        color: apollo.color.white
                    }}
                >
                    {getUserInitials()}
                </div>
                <span 
                    className="text-sm font-medium hidden md:block"
                    style={{ color: apollo.color.textPrimary }}
                >
                    {getUserDisplayName()}
                </span>
                <RiArrowDownSLine 
                    className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                    style={{ color: apollo.color.iconGray }}
                />
            </button>

            {isOpen && (
                <div 
                    className="absolute right-0 mt-2 w-56 rounded-xl overflow-hidden z-50"
                    style={{ 
                        backgroundColor: apollo.color.white,
                        boxShadow: apollo.shadow.lg,
                        border: `1px solid ${apollo.color.borderDefault}`,
                    }}
                    data-testid="user-menu-dropdown"
                >
                    <div 
                        className="px-4 py-3"
                        style={{ borderBottom: `1px solid ${apollo.color.borderSubtle}` }}
                    >
                        <div className="text-sm font-medium" style={{ color: apollo.color.textPrimary }}>
                            {getUserDisplayName()}
                        </div>
                        <div className="text-xs" style={{ color: apollo.color.textSecondary }}>
                            {user?.email}
                        </div>
                    </div>
                    <div className="py-1">
                        <button
                            onClick={() => { setIsOpen(false); signOut(); }}
                            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm transition-all hover:bg-gray-50"
                            style={{ color: apollo.color.textPrimary }}
                            data-testid="sign-out-button"
                        >
                            <RiLogoutBoxRLine className="w-4 h-4" style={{ color: apollo.color.iconGray }} />
                            Sign Out
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

// EntitySwitcher - Compact dropdown for switching between entities
const EntitySwitcher = ({ 
    currentEntity, 
    entities, 
    entityType, 
    entityTypeLabels, 
    onSelect, 
    isLoading,
    onBackToList
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const containerRef = useRef(null);
    const searchInputRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (!isOpen) return undefined;
        const timeoutId = setTimeout(() => {
            if (searchInputRef.current) {
                searchInputRef.current.focus();
            }
        }, 50);
        return () => clearTimeout(timeoutId);
    }, [isOpen]);

    const filteredEntities = useMemo(() => {
        if (!searchTerm) return entities;
        const lowerTerm = searchTerm.toLowerCase();
        return entities.filter(entity => 
            entity.label.toLowerCase().includes(lowerTerm) ||
            entity.id.toLowerCase().includes(lowerTerm)
        );
    }, [entities, searchTerm]);

    const handleSelect = (entityId) => {
        onSelect(entityId);
        setIsOpen(false);
        setSearchTerm('');
    };

    return (
        <div className="relative" ref={containerRef}>
            {/* Entity Name Button */}
            <button
                type="button"
                onClick={() => !isLoading && setIsOpen(!isOpen)}
                disabled={isLoading}
                className="flex items-center gap-2 px-1 py-1 rounded-lg transition-all hover:bg-gray-50"
                style={{ maxWidth: '280px' }}
                data-testid="entity-switcher-button"
            >
                {/* Entity Avatar */}
                <span 
                    className="w-9 h-9 rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0"
                    style={{ 
                        backgroundColor: apollo.color.purple1,
                        color: apollo.color.primary
                    }}
                >
                    {isLoading ? '...' : (currentEntity?.label?.charAt(0).toUpperCase() || '?')}
                </span>
                
                {/* Entity Name & Type */}
                <div className="text-left min-w-0">
                    <div 
                        className="text-base font-semibold truncate"
                        style={{ color: apollo.color.textPrimary }}
                        data-testid="entity-switcher-current-name"
                    >
                        {isLoading ? 'Loading...' : (currentEntity?.label || 'Select')}
                    </div>
                    <div 
                        className="text-xs flex items-center gap-1"
                        style={{ color: apollo.color.textSecondary }}
                    >
                        <EntityIcon type={entityType} className="w-3 h-3" />
                        {entityTypeLabels.singular}
                    </div>
                </div>
                
                {/* Dropdown Arrow */}
                <RiArrowDownSLine 
                    className="w-5 h-5 flex-shrink-0" 
                    style={{ 
                        transition: apollo.transition.normal,
                        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        color: apollo.color.iconGray,
                    }} 
                />
            </button>

            {/* Dropdown Menu */}
            {isOpen && !isLoading && (
                <div 
                    className="absolute left-0 w-80 flex flex-col max-h-[420px] z-50"
                    style={{
                        backgroundColor: apollo.color.bgPrimary,
                        border: `1px solid ${apollo.color.borderDefault}`,
                        borderRadius: apollo.radius.lg,
                        boxShadow: apollo.shadow.lg,
                        marginTop: '8px',
                    }}
                    data-testid="entity-switcher-dropdown"
                >
                    {/* Header with back button */}
                    <div 
                        className="px-4 py-3 flex items-center justify-between"
                        style={{ borderBottom: `1px solid ${apollo.color.borderSubtle}` }}
                    >
                        <span className="font-semibold" style={{ color: apollo.color.textPrimary }}>
                            Switch {entityTypeLabels.singular}
                        </span>
                        <button
                            onClick={() => { setIsOpen(false); onBackToList(); }}
                            className="text-sm flex items-center gap-1 px-2 py-1 rounded transition-all hover:bg-gray-100"
                            style={{ color: apollo.color.linkDefault }}
                            data-testid="view-all-button"
                        >
                            View All
                            <RiArrowRightSLine className="w-4 h-4" />
                        </button>
                    </div>

                    {/* Search */}
                    {entities.length > 5 && (
                        <div className="p-3">
                            <div className="relative">
                                <RiSearchLine 
                                    className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" 
                                    style={{ color: apollo.color.iconGray }}
                                />
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    placeholder={`Search ${entityTypeLabels.plural.toLowerCase()}...`}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-9 pr-3 py-2.5 text-sm rounded-lg focus:outline-none transition-all"
                                    style={{ 
                                        backgroundColor: apollo.color.bgSecondary,
                                        color: apollo.color.textPrimary,
                                        border: `1px solid transparent`,
                                    }}
                                    data-testid="entity-search-input"
                                    onFocus={(e) => {
                                        e.target.style.backgroundColor = apollo.color.white;
                                        e.target.style.borderColor = apollo.color.primary;
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.backgroundColor = apollo.color.bgSecondary;
                                        e.target.style.borderColor = 'transparent';
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    {/* Entity List */}
                    <div className="overflow-y-auto flex-1 px-2 pb-2">
                        {filteredEntities.length > 0 ? (
                            <div className="space-y-0.5">
                                {filteredEntities.map(entity => {
                                    const isSelected = entity.id === currentEntity?.id;
                                    return (
                                        <button
                                            key={entity.id}
                                            onClick={() => handleSelect(entity.id)}
                                            className="w-full px-3 py-2.5 rounded-lg transition-all text-left flex items-center gap-3"
                                            style={{
                                                backgroundColor: isSelected ? apollo.color.selectedBg : 'transparent',
                                            }}
                                            data-testid={`entity-option-${entity.id}`}
                                            onMouseOver={(e) => {
                                                if (!isSelected) e.currentTarget.style.backgroundColor = apollo.color.bgSecondary;
                                            }}
                                            onMouseOut={(e) => {
                                                if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                                            }}
                                        >
                                            <span 
                                                className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0"
                                                style={{ 
                                                    backgroundColor: isSelected ? apollo.color.primary : apollo.color.purple1,
                                                    color: isSelected ? apollo.color.white : apollo.color.primary
                                                }}
                                            >
                                                {entity.label.charAt(0).toUpperCase()}
                                            </span>
                                            <span 
                                                className="truncate flex-1"
                                                style={{ 
                                                    color: isSelected ? apollo.color.primary : apollo.color.textPrimary,
                                                    fontWeight: isSelected ? 600 : 400
                                                }}
                                                data-testid={`entity-option-name-${entity.id}`}
                                            >
                                                {entity.label}
                                            </span>
                                            {isSelected && (
                                                <RiCheckLine className="w-4 h-4 flex-shrink-0" style={{ color: apollo.color.primary }} />
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="px-4 py-8 text-center text-sm" style={{ color: apollo.color.textSecondary }}>
                                No {entityTypeLabels.plural.toLowerCase()} found
                            </div>
                        )}
                    </div>

                    {/* Footer count */}
                    <div 
                        className="px-4 py-2 text-xs"
                        style={{ 
                            borderTop: `1px solid ${apollo.color.borderSubtle}`,
                            color: apollo.color.textSecondary,
                            backgroundColor: apollo.color.bgSecondary
                        }}
                    >
                        {entities.length} {entityTypeLabels.plural.toLowerCase()} available
                    </div>
                </div>
            )}
        </div>
    );
};

// NavigationTabs - Horizontal tabs for sub-views
const NavigationTabs = ({ tabs, activeTab, entityId, entityType }) => {
    const navigate = useNavigate();
    
    const handleTabClick = (tabId) => {
        if (entityType === 'client') {
            navigate(`${ROUTES.ACCOUNTS}/${entityId}/${tabId}`);
        } else if (entityType === 'department') {
            navigate(`${ROUTES.OPERATIONS}/${entityId}/${tabId}`);
        } else if (entityType === 'staff') {
            navigate(`/staff/${entityId}/${tabId}`);
        }
    };

    return (
        <div className="flex items-center gap-1" data-testid="navigation-tabs">
            {tabs.map(tab => {
                const isActive = activeTab === tab.id;
                const Icon = tab.icon;
                return (
                    <button
                        key={tab.id}
                        onClick={() => handleTabClick(tab.id)}
                        className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-all"
                        style={{
                            backgroundColor: isActive ? apollo.color.selectedBg : 'transparent',
                            color: isActive ? apollo.color.primary : apollo.color.textSecondary,
                        }}
                        aria-current={isActive ? 'page' : undefined}
                        onMouseOver={(e) => {
                            if (!isActive) {
                                e.currentTarget.style.backgroundColor = apollo.color.bgSecondary;
                            }
                        }}
                        onMouseOut={(e) => {
                            if (!isActive) {
                                e.currentTarget.style.backgroundColor = 'transparent';
                            }
                        }}
                        data-testid={`nav-tab-${tab.id}`}
                    >
                        <Icon className="w-4 h-4" />
                        {tab.label}
                    </button>
                );
            })}
        </div>
    );
};

// Context for filter expansion state
const EntityNavigationContext = React.createContext({ filtersExpanded: false, setFiltersExpanded: () => {} });

export const useEntityNavigationContext = () => {
    const context = React.useContext(EntityNavigationContext);
    if (!context) {
        // Fallback if used outside provider
        return { filtersExpanded: false, setFiltersExpanded: () => {} };
    }
    return context;
};

// Provider component to wrap Dashboard content
export const EntityNavigationProvider = ({ children }) => {
    const [filtersExpanded, setFiltersExpanded] = useState(false);
    
    // Memoize the context value to prevent unnecessary re-renders
    const contextValue = useMemo(() => ({ 
        filtersExpanded, 
        setFiltersExpanded 
    }), [filtersExpanded]);
    
    return (
        <EntityNavigationContext.Provider value={contextValue}>
            {children}
        </EntityNavigationContext.Provider>
    );
};

// Main EntityNavigation component
const EntityNavigation = ({
    allClients = [],
    allDepartmentIds = [],
    allStaff = [],
    clientsLoaded = false,
    clientId,
    staffId,
    departmentId,
    user,
    signOut
}) => {
    // Get filter expansion state from context
    const { filtersExpanded, setFiltersExpanded } = useEntityNavigationContext();
    const location = useLocation();
    
    const entityNavProps = {
        allClients,
        allDepartmentIds,
        allStaff,
        clientsLoaded,
        clientId,
        staffId,
        departmentId
    };

    const {
        entityType,
        currentEntity,
        currentEntityId,
        entities,
        entityTypeLabels,
        navigateToEntity,
        navigateToList,
        isLoading
    } = useEntityNavigation(entityNavProps);

    // Get current path segments
    const pathSegments = location.pathname.split('/').filter(p => p);
    const activeTab = pathSegments.length > 2 ? pathSegments[2] : 'overview';

    // Don't render navigation UI if not on an entity detail page
    if (!entityType) {
        return null;
    }

    const tabs = NAV_TABS[entityType] || NAV_TABS.client;

    return (
        <div className="-mx-4 md:-mx-8 -mt-4 md:-mt-8 mb-2" data-testid="entity-navigation-header">
                {/* Sticky Header */}
                <header 
                    className="sticky top-0 z-10"
                    style={{ 
                        backgroundColor: apollo.color.white,
                        boxShadow: apollo.shadow.sm,
                    }}
                >
                    <div className="px-4 md:px-8 py-3 flex items-center gap-3">
                        {/* Back to List */}
                        <button
                            onClick={navigateToList}
                            className="p-2 rounded-lg transition-all hover:bg-gray-100 flex-shrink-0"
                            style={{ color: apollo.color.textSecondary }}
                            title={`Back to all ${entityTypeLabels.plural.toLowerCase()}`}
                            data-testid="back-to-list-button"
                        >
                            <RiArrowLeftLine className="w-5 h-5" />
                        </button>

                        {/* Divider */}
                        <div className="w-px h-8 bg-gray-200 flex-shrink-0" />

                        {/* Entity Switcher */}
                        <EntitySwitcher
                            currentEntity={currentEntity}
                            entities={entities}
                            entityType={entityType}
                            entityTypeLabels={entityTypeLabels}
                            onSelect={navigateToEntity}
                            isLoading={isLoading}
                            onBackToList={navigateToList}
                        />

                        {/* Spacer */}
                        <div className="flex-1" />

                        {/* Navigation Tabs */}
                        <NavigationTabs
                            tabs={tabs}
                            activeTab={activeTab}
                            entityId={currentEntityId}
                            entityType={entityType}
                        />

                        {/* Divider and Filter Toggle - Hidden for Staff view */}
                        {entityType !== 'staff' && (
                            <>
                                <div className="w-px h-8 bg-gray-200 flex-shrink-0" />

                                <button
                                    onClick={() => setFiltersExpanded(!filtersExpanded)}
                                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all"
                                    style={{
                                        backgroundColor: filtersExpanded 
                                            ? apollo.color.selectedBg 
                                            : 'transparent',
                                        color: filtersExpanded 
                                            ? apollo.color.primary 
                                            : apollo.color.textSecondary,
                                    }}
                                    onMouseOver={(e) => {
                                        if (!filtersExpanded) e.currentTarget.style.backgroundColor = apollo.color.bgSecondary;
                                    }}
                                    onMouseOut={(e) => {
                                        if (!filtersExpanded) {
                                            e.currentTarget.style.backgroundColor = 'transparent';
                                        }
                                    }}
                                    data-testid="filter-toggle-button"
                                >
                                    <RiFilterLine className="w-4 h-4" />
                                    Filters
                                    <RiArrowDownSLine 
                                        className={`w-4 h-4 transition-transform ${filtersExpanded ? 'rotate-180' : ''}`}
                                    />
                                </button>
                            </>
                        )}

                        {/* Divider */}
                        <div className="w-px h-8 bg-gray-200 flex-shrink-0" />

                        {/* User Menu */}
                        {user && signOut && (
                            <UserMenu user={user} signOut={signOut} />
                        )}
                    </div>
                </header>
            </div>
    );
};

export default EntityNavigation;
